import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const DELIMITERS = [
  { key: "comma", char: ",", labelKey: "csv_json_delim_comma" },
  { key: "semicolon", char: ";", labelKey: "csv_json_delim_semicolon" },
  { key: "tab", char: "\t", labelKey: "csv_json_delim_tab" },
  { key: "pipe", char: "|", labelKey: "csv_json_delim_pipe" },
];

const DEFAULT_CSV = `id,name,role,active
1,Alex,Engineer,true
2,Sam,Designer,false
3,Riya,Product Manager,true`;

const DEFAULT_JSON = `[
  { "id": 1, "name": "Alex", "role": "Engineer", "active": true },
  { "id": 2, "name": "Sam", "role": "Designer", "active": false },
  { "id": 3, "name": "Riya", "role": "Product Manager", "active": true }
]`;

const DETECT_CANDIDATES = [",", ";", "\t", "|"];

const detectDelimiter = (text) => {
  const stripped = text.replace(/^\uFEFF/, "");
  if (!stripped.trim()) return null;

  const lines = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < stripped.length && lines.length < 20; i += 1) {
    const ch = stripped[i];
    if (inQuotes) {
      if (ch === '"') {
        if (stripped[i + 1] === '"') {
          current += '""';
          i += 1;
        } else {
          inQuotes = false;
          current += ch;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
      current += ch;
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && stripped[i + 1] === "\n") i += 1;
      if (current.trim() !== "") lines.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current.trim() !== "") lines.push(current);

  const sample = lines.slice(0, 10);
  if (sample.length === 0) return null;

  const countOutsideQuotes = (line, delim) => {
    let count = 0;
    let q = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (q) {
        if (ch === '"') {
          if (line[i + 1] === '"') i += 1;
          else q = false;
        }
      } else if (ch === '"') {
        q = true;
      } else if (ch === delim) {
        count += 1;
      }
    }
    return count;
  };

  let best = null;
  DETECT_CANDIDATES.forEach((delim) => {
    const counts = sample.map((l) => countOutsideQuotes(l, delim));
    const first = counts[0];
    if (first === 0) return;
    const consistent = counts.every((c) => c === first);
    const total = counts.reduce((a, b) => a + b, 0);
    const score = (consistent ? 1000 : 0) + total;
    if (!best || score > best.score) {
      best = { delim, score, consistent, perLine: first };
    }
  });

  if (!best) return null;
  return DETECT_CANDIDATES.indexOf(best.delim);
};

const parseCsv = (text, delimiter) => {
  const rows = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      row.push(field);
      field = "";
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      if (row.length > 1 || row[0] !== "") rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    if (row.length > 1 || row[0] !== "") rows.push(row);
  }
  return rows;
};

const coerce = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  if (raw === "null") return null;
  if (raw === "true") return true;
  if (raw === "false") return false;
  if (/^-?\d+$/.test(raw)) {
    const n = Number(raw);
    if (Number.isSafeInteger(n)) return n;
  }
  if (/^-?\d*\.\d+(?:[eE][+-]?\d+)?$/.test(raw)) {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return raw;
};

const escapeCsvField = (value, delimiter) => {
  if (value === null || value === undefined) return "";
  const str = typeof value === "string" ? value : JSON.stringify(value);
  if (
    str.includes(delimiter) ||
    str.includes('"') ||
    str.includes("\n") ||
    str.includes("\r")
  ) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const csvToJson = (input, opts) => {
  const text = input.replace(/^\uFEFF/, "");
  if (!text.trim()) return { ok: true, output: "[]" };
  const rows = parseCsv(text, opts.delimiter);
  if (rows.length === 0) return { ok: true, output: "[]" };

  const headers = opts.hasHeader
    ? rows[0].map((h) => h.trim())
    : rows[0].map((_, i) => `column_${i + 1}`);
  const dataRows = opts.hasHeader ? rows.slice(1) : rows;

  const objects = dataRows.map((cols) => {
    const obj = {};
    headers.forEach((h, i) => {
      const raw = cols[i] ?? "";
      obj[h] = opts.coerceTypes ? coerce(raw) : raw;
    });
    return obj;
  });

  const indent = opts.pretty ? 2 : 0;
  return { ok: true, output: JSON.stringify(objects, null, indent) };
};

const jsonToCsv = (input, opts) => {
  if (!input.trim()) return { ok: true, output: "" };
  let data;
  try {
    data = JSON.parse(input);
  } catch (err) {
    return { ok: false, errorKey: "csv_json_error_invalid_json", errorDetail: err.message };
  }
  if (!Array.isArray(data)) {
    if (data && typeof data === "object") {
      data = [data];
    } else {
      return { ok: false, errorKey: "csv_json_error_not_array" };
    }
  }
  if (data.length === 0) return { ok: true, output: "" };

  const headerSet = new Set();
  data.forEach((row) => {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      Object.keys(row).forEach((k) => headerSet.add(k));
    }
  });
  if (headerSet.size === 0) {
    return { ok: false, errorKey: "csv_json_error_no_objects" };
  }
  const headers = Array.from(headerSet);
  const lines = [];
  if (opts.hasHeader) {
    lines.push(headers.map((h) => escapeCsvField(h, opts.delimiter)).join(opts.delimiter));
  }
  data.forEach((row) => {
    const cols = headers.map((h) => {
      const v = row && typeof row === "object" ? row[h] : undefined;
      return escapeCsvField(v, opts.delimiter);
    });
    lines.push(cols.join(opts.delimiter));
  });
  return { ok: true, output: lines.join("\n") };
};

const CsvJsonConverter = () => {
  const { t } = useTranslation();
  const [direction, setDirection] = useState("csv-to-json");
  const [csvText, setCsvText] = useState(DEFAULT_CSV);
  const [jsonText, setJsonText] = useState(DEFAULT_JSON);
  const [delimiterKey, setDelimiterKey] = useState("comma");
  const [hasHeader, setHasHeader] = useState(true);
  const [coerceTypes, setCoerceTypes] = useState(true);
  const [pretty, setPretty] = useState(true);

  const delimiter = useMemo(
    () => DELIMITERS.find((d) => d.key === delimiterKey)?.char ?? ",",
    [delimiterKey],
  );

  const result = useMemo(() => {
    if (direction === "csv-to-json") {
      return csvToJson(csvText, { delimiter, hasHeader, coerceTypes, pretty });
    }
    return jsonToCsv(jsonText, { delimiter, hasHeader });
  }, [direction, csvText, jsonText, delimiter, hasHeader, coerceTypes, pretty]);

  const isCsvToJson = direction === "csv-to-json";
  const inputValue = isCsvToJson ? csvText : jsonText;
  const setInput = isCsvToJson ? setCsvText : setJsonText;

  const swap = () => {
    if (result.ok && result.output) {
      if (isCsvToJson) {
        setJsonText(result.output);
      } else {
        setCsvText(result.output);
      }
    }
    setDirection(isCsvToJson ? "json-to-csv" : "csv-to-json");
  };

  const copy = async () => {
    if (!result.ok || !result.output) {
      toast.error(t("csv_json_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(result.output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const download = () => {
    if (!result.ok || !result.output) {
      toast.error(t("csv_json_nothing_to_copy"));
      return;
    }
    const filename = isCsvToJson ? "converted.json" : "converted.csv";
    const mime = isCsvToJson ? "application/json" : "text/csv";
    const blob = new Blob([result.output], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clear = () => setInput("");

  const autoDetect = () => {
    if (!csvText.trim()) {
      toast.error(t("csv_json_autodetect_empty"));
      return;
    }
    const idx = detectDelimiter(csvText);
    if (idx === null) {
      toast.error(t("csv_json_autodetect_none"));
      return;
    }
    const detected = DELIMITERS[idx];
    setDelimiterKey(detected.key);
    toast.success(t("csv_json_autodetect_found", { name: t(detected.labelKey) }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("csv_json_title")}
            </h1>
            <p className="text-gray-600">{t("csv_json_intro")}</p>

            <div className="flex flex-wrap items-center gap-2">
              <StandardButton
                onClick={() => setDirection("csv-to-json")}
                type={isCsvToJson ? "primary" : "secondary"}
                variant={isCsvToJson ? undefined : "outlined"}
                size="small"
              >
                {t("csv_json_dir_csv_to_json")}
              </StandardButton>
              <StandardButton
                onClick={() => setDirection("json-to-csv")}
                type={!isCsvToJson ? "primary" : "secondary"}
                variant={!isCsvToJson ? undefined : "outlined"}
                size="small"
              >
                {t("csv_json_dir_json_to_csv")}
              </StandardButton>
              <StandardButton
                onClick={swap}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("csv_json_swap")}
              </StandardButton>
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("csv_json_delimiter_label")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {DELIMITERS.map((d) => (
                    <StandardButton
                      key={d.key}
                      onClick={() => setDelimiterKey(d.key)}
                      type={delimiterKey === d.key ? "primary" : "secondary"}
                      variant={delimiterKey === d.key ? undefined : "outlined"}
                      size="small"
                    >
                      <span className="font-mono mr-1">
                        {d.char === "\t" ? "\\t" : d.char}
                      </span>
                      {t(d.labelKey)}
                    </StandardButton>
                  ))}
                  {isCsvToJson && (
                    <StandardButton
                      onClick={autoDetect}
                      type="secondary"
                      variant="outlined"
                      size="small"
                      title={t("csv_json_autodetect_hint")}
                    >
                      {t("csv_json_autodetect")}
                    </StandardButton>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("csv_json_has_header")}
                </label>
                {isCsvToJson && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={coerceTypes}
                      onChange={(e) => setCoerceTypes(e.target.checked)}
                      className="h-4 w-4"
                    />
                    {t("csv_json_coerce_types")}
                  </label>
                )}
                {isCsvToJson && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={pretty}
                      onChange={(e) => setPretty(e.target.checked)}
                      className="h-4 w-4"
                    />
                    {t("csv_json_pretty_print")}
                  </label>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {isCsvToJson
                    ? t("csv_json_input_label_csv")
                    : t("csv_json_input_label_json")}
                </label>
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  disabled={!inputValue}
                >
                  {t("csv_json_clear")}
                </StandardButton>
              </div>
              <textarea
                value={inputValue}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  isCsvToJson
                    ? t("csv_json_placeholder_csv")
                    : t("csv_json_placeholder_json")
                }
                rows={10}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {isCsvToJson
                    ? t("csv_json_output_label_json")
                    : t("csv_json_output_label_csv")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={download}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!result.ok || !result.output}
                  >
                    {t("csv_json_download")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!result.ok || !result.output}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              {result.ok ? (
                <pre className="w-full min-h-[6rem] max-h-[24rem] overflow-auto p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre text-gray-800">
                  {result.output || (
                    <em className="text-gray-400 not-italic">
                      {t("csv_json_output_empty")}
                    </em>
                  )}
                </pre>
              ) : (
                <p className="text-sm text-red-600 break-all">
                  {t(result.errorKey)}
                  {result.errorDetail ? ` — ${result.errorDetail}` : ""}
                </p>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("csv_json_note_quotes")}</p>
              <p>{t("csv_json_note_coerce")}</p>
              <p>{t("csv_json_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CsvJsonConverter;
